import { useMemo } from "react";
import {
  ReminderView,
  PatientReminderData,
  ArticleViewData,
  AggregatedArticleData,
} from "../types/reminderTypes";
import { getSearchConfig } from "../config/viewConfig";

/**
 * Generic interface for search results
 */
export interface SearchResult<TData> {
  filteredData: TData[];
  searchTerm: string;
  totalResults: number;
  hasActiveSearch: boolean;
  isLoading: boolean;
}

/**
 * Custom hook for view-aware search functionality
 * Provides optimized, memoized search results based on the current view and data type
 */
export function useViewSearch<
  TData extends PatientReminderData | ArticleViewData | AggregatedArticleData
>(
  view: ReminderView,
  data: TData[],
  searchTerm: string,
  isDataLoading: boolean = false
): SearchResult<TData> {
  // Memoize search configuration to prevent unnecessary recalculations
  const searchConfig = useMemo(() => getSearchConfig(view), [view]);

  // Memoize search function to prevent unnecessary recalculations
  const searchFunction = useMemo(
    () => searchConfig.searchFunction,
    [searchConfig]
  );

  // Apply search filtering with proper memoization for performance
  const searchResult = useMemo((): SearchResult<TData> => {
    const trimmedSearchTerm = searchTerm?.trim() || "";
    const hasActiveSearch = trimmedSearchTerm.length > 0;

    // If no search term, return all data
    if (!hasActiveSearch) {
      return {
        filteredData: data,
        searchTerm: trimmedSearchTerm,
        totalResults: data.length,
        hasActiveSearch: false,
        isLoading: isDataLoading,
      };
    }

    // Apply view-specific search function with improved type safety
    let filteredData: TData[];
    try {
      filteredData = searchFunction(trimmedSearchTerm, data) as TData[];
    } catch (error) {
      console.error(`Search error for view ${view}:`, error);
      filteredData = data; // Fallback to unfiltered data
    }

    return {
      filteredData,
      searchTerm: trimmedSearchTerm,
      totalResults: filteredData.length,
      hasActiveSearch: true,
      isLoading: isDataLoading,
    };
  }, [data, searchTerm, searchFunction, isDataLoading, view]);

  return searchResult;
}

/**
 * Utility hook for getting search metadata for a specific view
 * Useful for components that need to display search information
 */
export function useSearchMetadata(view: ReminderView) {
  return useMemo(() => {
    const config = getSearchConfig(view);
    return {
      placeholder: config.placeholder,
      searchFields: config.searchFields,
      description: config.description,
    };
  }, [view]);
}

/**
 * Type-safe search hook specifically for Patient data
 */
export function usePatientSearch(
  data: PatientReminderData[],
  searchTerm: string,
  isLoading?: boolean
): SearchResult<PatientReminderData> {
  return useViewSearch(ReminderView.PATIENTS, data, searchTerm, isLoading);
}

/**
 * Type-safe search hook specifically for Article data
 * Supports DUAL SEARCH: article names + patient information
 */
export function useArticleSearch(
  data: ArticleViewData[],
  searchTerm: string,
  isLoading?: boolean
): SearchResult<ArticleViewData> {
  return useViewSearch(ReminderView.ARTICLES, data, searchTerm, isLoading);
}

/**
 * Type-safe search hook specifically for Aggregated data
 */
export function useAggregatedSearch(
  data: AggregatedArticleData[],
  searchTerm: string,
  isLoading?: boolean
): SearchResult<AggregatedArticleData> {
  return useViewSearch(ReminderView.AGGREGATED, data, searchTerm, isLoading);
}
